// src/pages/CategoriesPage.js
import React, { useState, useEffect } from 'react';
import {
    Container,
    List,
    ListItem,
    ListItemText,
    Button,
    TextField,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Box,
    LinearProgress,
    Typography,
    Collapse,
    Snackbar,
    Alert,
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    ExpandMore as ExpandMoreIcon,
    ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';
import { useCategories, useCategoryMutations, uploadFile } from '../services/api';
import { resolveImageUrl } from '../utils/imageUrl';

const CategoriesPage = () => {
    const { data: categories = [], isLoading, error } = useCategories();
    const {
        createMutation,
        updateMutation,
        deleteMutation
    } = useCategoryMutations();
    const [open, setOpen] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [parentId, setParentId] = useState(null);
    const [expanded, setExpanded] = useState({});
    const [formData, setFormData] = useState({
        name: '',
        nameArabic: '',
        level: '0',
        imageUrl: '',
        isActive: true,
    });
    const [selectedFile, setSelectedFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });

    const handleOpen = (category = null, parent = null) => {
        // Reset file input when opening the dialog
        setSelectedFile(null);
        if (category) {
            setFormData({
                name: category.name,
                nameArabic: category.nameArabic,
                level: category.level,
                imageUrl: resolveImageUrl(category.imageUrl) || '',
                isActive: category.isActive,
            });
            setEditingId(category.id);
            setParentId(parent?.id || null);
        } else {
            setFormData({
                name: '',
                nameArabic: '',
                level: parent ? String(Number(parent.level) + 1) : '0',
                imageUrl: '',
                isActive: true,
            });
            setEditingId(null);
            setParentId(parent?.id || null);
        }
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value,
        });
    };

    const handleFileChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedFile(file);
        }
    };


    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setIsUploading(true);
            const data = { ...formData };
            // Upload file if selected
            if (selectedFile) {
                const uploadResponse = await uploadFile(selectedFile);
                if (uploadResponse.status) {
                    data.imageUrl = uploadResponse.data.url;
                } else {
                    throw new Error(uploadResponse.message || 'Failed to upload image');
                }
            }
            
            if (data.imageUrl) {
                data.imageUrl = resolveImageUrl(data.imageUrl);
            }

            if (parentId) {
                data.parentId = parentId;
            }

            if (editingId) {
                await updateMutation.mutateAsync({ id: editingId, ...data });
                showSnackbar('Category updated successfully');
            } else {
                await createMutation.mutateAsync(data);
                showSnackbar('Category created successfully');
            }
            
            // Reset form and close
            setSelectedFile(null);
            handleClose();
        } catch (error) {
            const message = error.response?.data?.message || error.message || 'An error occurred';
            showSnackbar(message, 'error');
        } finally {
            setIsUploading(false);
        }
    };


    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this category?')) {
            try {
                await deleteMutation.mutateAsync(id);
                showSnackbar('Category deleted successfully');
            } catch (error) {
                showSnackbar('Failed to delete category', 'error');
            }
        }
    };

    const toggleExpand = (id) => {
        setExpanded((prev) => ({
            ...prev,
            [id]: !prev[id],
        }));
    };

    const showSnackbar = (message, severity = 'success') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const renderCategories = (items, level = 0) => {
        return items.map((category) => (
            <React.Fragment key={category.id}>
                <ListItem
                    sx={{ pl: level * 4 }}
                    secondaryAction={
                        <Box>
                            <IconButton
                                edge="end"
                                onClick={() => handleOpen(category, category.parentId)}
                                sx={{ mr: 1 }}
                            >
                                <EditIcon />
                            </IconButton>
                            <IconButton
                                edge="end"
                                onClick={() => handleDelete(category.id)}
                                sx={{ mr: 1 }}
                            >
                                <DeleteIcon color="error" />
                            </IconButton>
                            {category.children && category.children.length > 0 && (
                                <IconButton
                                    edge="end"
                                    onClick={() => toggleExpand(category.id)}
                                >
                                    {expanded[category.id] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                </IconButton>
                            )}
                        </Box>
                    }
                >
                    <ListItemText
                        primary={`${category.name} (${category.nameArabic})`}
                        // secondary={`Level: ${category.level}`}
                        secondary={
                            <>
                            Level: {category.level}
                            <br />
                            <Button
                                size="small"
                                startIcon={<AddIcon />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleOpen(null, category);
                            }}>
                               Add Subcategory
                            </Button>
                            </>
                        }
                    />
                </ListItem>
                {category.children && category.children.length > 0 && (
                    <Collapse in={expanded[category.id]} timeout="auto" unmountOnExit>
                        <List component="div" disablePadding>
                            {renderCategories(category.children, level + 1)}
                        </List>
                    </Collapse>
                )}
            </React.Fragment>
        ));
    };
    if (isLoading) {
        return <LinearProgress />;
    }

    return (
        <Container>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h4">Categories</Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpen()}
                >
                    Add Category
                </Button>
            </Box>

            <List>
                {categories.length > 0 ? (
                    renderCategories(categories)
                ) : (
                    <Typography>No categories found</Typography>
                )}
            </List>

            <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
                <form onSubmit={handleSubmit}>
                    <DialogTitle>
                        {editingId ? 'Edit Category' : 'Add New Category'}
                        {parentId && <div>Parent: {parentId}</div>}
                    </DialogTitle>
                    <DialogContent>
                        <TextField
                            autoFocus
                            margin="dense"
                            name="name"
                            label="Name"
                            fullWidth
                            required
                            value={formData.name}
                            onChange={handleChange}
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            margin="dense"
                            name="nameArabic"
                            label="Arabic Name"
                            fullWidth
                            required
                            value={formData.nameArabic}
                            onChange={handleChange}
                            sx={{ mb: 2 }}
                        />
                        <TextField
                            margin="dense"
                            name="level"
                            label="Level"
                            type="number"
                            fullWidth
                            required
                            value={formData.level}
                            onChange={handleChange}
                            disabled={!!editingId}
                            sx={{ mb: 2 }}
                        />
                        <Box sx={{ mb: 2 }}>
                            <input
                                accept="image/*"
                                style={{ display: 'none' }}
                                id="category-image-upload"
                                type="file"
                                onChange={handleFileChange}
                            />
                            <label htmlFor="category-image-upload">
                                <Button
                                    variant="outlined"
                                    component="span"
                                    fullWidth
                                    sx={{ mb: 1 }}
                                >
                                    {selectedFile ? 'Change Image' : 'Upload Image'}
                                </Button>
                            </label>
                            {selectedFile && (
                                <Typography variant="caption" display="block">
                                    {selectedFile.name}
                                </Typography>
                            )}
                            {formData.imageUrl && !selectedFile && (
                                <Box sx={{ mt: 1 }}>
                                    <Button
                                        size="small"
                                        color="primary"
                                        onClick={(e) => {
                                            window.open(resolveImageUrl(formData.imageUrl), '_blank');
                                        }}
                                    >
                                        <Typography variant="caption" display="block">
                                            Current image: {formData.imageUrl}
                                        </Typography>
                                    </Button>
                                    <Button
                                        size="small"
                                        color="error"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            setFormData({ ...formData, imageUrl: '' });
                                        }}
                                    >
                                        Remove Image
                                    </Button>
                                </Box>
                            )}
                        </Box>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleClose}>Cancel</Button>
                        <Button 
                            type="submit" 
                            variant="contained"
                            disabled={isUploading}
                        >
                            {isUploading ? 'Uploading...' : (editingId ? 'Update' : 'Create')}
                        </Button>
                    </DialogActions>
                </form>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Container>
    );
};

export default CategoriesPage;